<template>
    <div class="mt-6 relative @container">
        <div class="relative">
            <div class="relative px-12 flex flex-col">
                <!-- SEARCH FORM -->
                <div class="rounded-lg flex flex-col justify-between">
                    <LoungeAirportSearch />
                </div>
                <!-- SEARCH FILTER BAR -->
                <LoungesFilterBar
                    v-if="searchState !== 'loading'"
                    class="mt-6"
                    @toggleMap="toggleOpenMap"
                    ƒo
                />
                <div id="top" class="hidden"></div>
                <div v-if="searchState === 'loading'" class="border-red-500">
                    <LoadingState
                        :loading="searchState === 'loading'"
                        class="relative mt-16"
                    />
                </div>
                <!-- SEARCH HEADER -->
                <div v-if="searchState !== 'loading'">
                    <LoungeSearchResultsHeader />
                </div>
                <!-- SEARCH RESULTS -->
                <LoungesSearchResults
                    v-if="!loading"
                    @page-changed="handlePageChanged"
                />
            </div>
        </div>
        <LoungeFilterModal />
        <div class="fixed inset-0 z-[999]" v-if="showMap">
            <Teleport to="body">
                <Suspense>
                    <Map
                        :loading="searchState === 'loading'"
                        :showMap="showMap"
                        :mapMarkers="mapMarkers"
                        :mapType="mapType"
                        :mapCenter="mapCenter"
                        @closeMap="handleCloseMap"
                    >
                        <template #actionbar>
                            <div
                                class="absolute gap-2 top-16 md:w-1/2 w-5/6 md:top-6 md:left-6 left-3 z-[9999] flex flex-col overflow-visible"
                                style="transform: translate3d(0, 0, 200px)"
                            >
                                <div
                                    class="bg-white rounded shadow p-2 flex items-center w-full"
                                >
                                    <div
                                        class="w-5 h-5 flex align-center text-[#808080] ml-4"
                                    >
                                        <Icon icon="search" />
                                    </div>
                                    <LoungesAutoSuggest
                                        inputClass="grow text-slate-800 font-medium border-none focus:ring-0 relative text-sm leading-tight placeholder:text-slate-500 placeholder:font-medium"
                                        :allSuggestions="airports"
                                        :inputValue="activeAirport?.name"
                                        valueKey="code"
                                        displayKey="name"
                                        @select="(v) => handleChangeAirport(v)"
                                        @submit="(v) => handleChangeAirport(v)"
                                    />
                                </div>
                                <LoungesFilterBar
                                    v-if="searchResultsCount"
                                    class="relative z-10"
                                    @toggleMap="toggleOpenMap"
                                />
                                <EmptyState
                                    v-if="
                                        searchState === 'empty' ||
                                        searchState === 'error'
                                    "
                                    title="No lounges found"
                                    description="No lounges found for this airport."
                                    :hasAction="false"
                                    class="relative bg-white rounded-lg shadow"
                                >
                                    <template #image>
                                        <div
                                            class="w-16 h-16 bg-slate-200 rounded-full justify-center items-center flex"
                                        >
                                            <Icon
                                                icon="search"
                                                svgClass="w-8 h-8"
                                            />
                                        </div>
                                    </template>
                                </EmptyState>
                            </div>
                        </template>
                        <template
                            #popup="{ closeHandler, infoWindowContent, show }"
                        >
                            <LoungeInfoWindow
                                :infoWindowContent="infoWindowContent"
                                :show="show"
                                @close="closeHandler"
                                @favoriteChanged="handleFavoriteChanged"
                                class="z-[9999]"
                            />
                        </template>
                    </Map>
                </Suspense>
            </Teleport>
        </div>
    </div>
</template>

<script>
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import { useLoungesStore } from '@/store/lounges';
import FavButton from '@/components/ui/fav-button/fav-button.vue';
import LoungeSearchResultsHeader from '@/components/tools/lounges/partials/lounge-search-results-header/lounge-search-results-header.vue';
import Icon from '@/components/ui/icon/icon.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import LoungeAirportSearch from '@/components/tools/lounges/lounges-search/lounge-airport-search/lounge-airport-search.vue';
import LoungesAutoSuggest from '@/components/tools/lounges/lounges-search/lounges-autosuggest/lounges-autosuggest.vue';
import LoungeInfoWindow from '@/components/tools/lounges/partials/lounge-infowindow/lounge-infowindow.vue';
import LoungesSearchResults from '@/components/tools/lounges/lounges-search/lounges-search-results/lounges-search-results.vue';
import LoungesFilterBar from '@/components/tools/lounges/lounges-search/lounges-filter-bar/lounges-filter-bar.vue';
import LoungeFilterModal from '@/components/tools/lounges/lounges-search/lounge-filter-modal/lounge-filter-modal.vue';
import Map from '@/components/tools/lounges/partials/lounge-map/lounge-map.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';

export default {
    name: 'LoungesSearch',
    components: {
        EmptyState,
        FavButton,
        Icon,
        LoadingState,
        LoungeFilterModal,
        LoungeAirportSearch,
        LoungesAutoSuggest,
        LoungeInfoWindow,
        LoungesSearchResults,
        LoungeSearchResultsHeader,
        LoungesFilterBar,
        Map,
    },
    setup() {
        const loungesStore = useLoungesStore();
        const {
            getFilterOptions,
            searchLounges,
            setActiveAirport,
            setLoading,
            setMapType,
            setShowMap,
            setSearchState,
            setSearchType,
            toggleFavorite,
        } = loungesStore;
        const {
            activeAirport,
            airports,
            showMap,
            loading,
            mapCenter,
            mapType,
            mapMarkers,
            searchResultsCount,
            searchResults,
            searchState,
            filteredSearchResults,
            error,
        } = storeToRefs(loungesStore);

        const isFavoriteAirport = ref(activeAirport.value?.favorite || false);
        const favoritesChanged = ref(false);
        const route = useRoute();
        const router = useRouter();

        const handleChangeAirport = (val) => {
            setActiveAirport(val);
            searchLounges();
        };

        const handlePageChanged = () => {
            const top = document.getElementById('top');

            if (top) {
                top.scrollIntoView({ behavior: 'smooth' });
            }
        };

        const handleToggleFavorite = async () => {
            if (!activeAirport.value?.id) {
                console.error(
                    'No valid airport to favorite:',
                    activeAirport.value
                );
                return;
            }
            isFavoriteAirport.value = !isFavoriteAirport.value;

            const res = await toggleFavorite(activeAirport.value, 'airport');
            if (!res || res.status !== 200) {
                isFavoriteAirport.value = !isFavoriteAirport.value;
            }
        };

        const toggleOpenMap = () => {
            const mapState = showMap.value;
            if (!mapState) {
                setMapType('searchResults');
            }
            setShowMap(!mapState);
        };

        const handleFavoriteChanged = () => {
            favoritesChanged.value = true;
        };

        const handleCloseMap = () => {
            if (favoritesChanged.value) {
                searchLounges();
            }
            setShowMap(false);
        };

        const setAirportFromRoute = () => {
            const params = route.query;

            if (params.airport && airports.value.length) {
                const airport = airports.value.find(
                    (a) => a.code === params.airport
                );

                if (airport) {
                    setActiveAirport(airport);
                    search();
                }
            }
        };

        const search = async () => {
            searchLounges();

            router.push(`/lounges/search?airport=${activeAirport.value.code}`);
        };

        const initializeData = async () => {
            if (!activeAirport.value) {
                setAirportFromRoute();
            }

            setTimeout(() => {
                setLoading(false);
            }, 500);
        };

        watch(
            [() => searchResults.value, () => error.value],
            ([newResults, newError]) => {
                if (newError) {
                    setSearchState('error');
                } else if (newResults === null) {
                    setSearchState('loading');
                } else if (
                    Object.keys(filteredSearchResults.value || {}).length === 0
                ) {
                    setSearchState('empty');
                } else {
                    setSearchState('results');
                }
            },
            { immediate: true }
        );

        watch(
            activeAirport,
            (newVal) => {
                isFavoriteAirport.value = newVal.favorite;
            },
            { deep: true }
        );

        onMounted(async () => {
            setSearchType('Find a lounge');
            await getFilterOptions();
            initializeData();
        });

        return {
            activeAirport,
            airports,
            isFavoriteAirport,
            loading,
            handleChangeAirport,
            handleCloseMap,
            handleFavoriteChanged,
            handlePageChanged,
            handleToggleFavorite,
            mapCenter,
            mapMarkers,
            mapType,
            toggleOpenMap,
            searchState,
            searchResultsCount,
            setActiveAirport,
            setShowMap,
            showMap,
        };
    },
};
</script>
